/* global LINKED_APPLICATIONS_EVENTAPP, LINKED_APPLICATIONS_REGISTER, LINKED_APPLICATIONS_LIVEDISPLAY  */

import {
  AndroidOutlined,
  AppleOutlined,
  EyeOutlined,
  FundProjectionScreenOutlined,
  LayoutOutlined,
  ProfileOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { Button as AntButton, Dropdown as AntDropdown } from 'antd';
import { addDays, format, parseISO } from 'date-fns';
import React from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { t } from 'ttag';

import useConfig from 'common/hooks/useConfig';
import { useEventQuery } from 'common/services/events';

import EventAppIcon from '@assets/app-icons/event-app.svg';
import LeadCaptureAppIcon from '@assets/app-icons/lead-capture.svg';
import BadgeONLogo from '@assets/badgeON-logo.svg';
import { useEventExpiryDate } from '@domains/Dashboard/useEventExpiryDate';
import { useEventAppsQuery } from '@domains/Organization/App/queries';
import { useEventDomain } from '@domains/Organization/CustomDomain/queries';
import { useSettingsQuery } from '@domains/WebsiteBuilder/ContentPages/queries';

import PreviewItem from './Item';

const Button = styled(AntButton)`
  margin-left: 12px;
  color: #1890ff;
  border-color: #1890ff;
`;

const Dropdown = styled(({ className, ...props }) => (
  <AntDropdown classNames={{ root: className }} {...props} />
))`
  .ant-dropdown-menu {
    // Remove the padding on the bottom so that highlights show up correctly
    padding: 0;
  }
  .ant-dropdown-menu-item-divider {
    // Remove the margin around dividers so that highlights show up correctly
    margin: 0;
  }
`;

export const useItems = () => {
  const { eventId, organizationId } = useParams();
  const config = useConfig();
  const isEventAppEnabled = config.isConfigEnabled('is_eventspace_enabled');
  const isWebsiteBuilderEnabled = config.isConfigEnabled('is_website_builder_enabled');
  const isRegistrationEnabled = config.isConfigEnabled('is_registration_v2_enabled');
  const isLiveDisplayEnabled = config.isConfigEnabled('is_livedisplay_enabled');
  const isCheckInEnabled = config.isConfigEnabled('is_checkin_enabled');
  const isLeadCaptureEnabled = config.isConfigEnabled('is_lead_capture_enabled');
  const eventappPostEventAccessDays = Number(config.get('eventapp_post_event_access_days')) ?? 0;

  const { data: event, isLoading: isLoadingEvent } = useEventQuery(eventId);
  const { data: apps, isLoading: isLoadingApps } = useEventAppsQuery(eventId);
  const { data: settings, isLoading: isLoadingSettings } = useSettingsQuery();
  const { data: customDomain, isLoading: isLoadingCustomDomain } = useEventDomain(
    organizationId,
    eventId,
  );
  const { getExpiryDate } = useEventExpiryDate(event?.data);

  const loading = isLoadingSettings || isLoadingCustomDomain || isLoadingEvent || isLoadingApps;

  if (loading) {
    return { items: [] };
  }

  const {
    data: { code: eventCode },
  } = event;
  const parsedEventEndDate = parseISO(getExpiryDate());
  const parsedEventAppEndDate = addDays(parsedEventEndDate, eventappPostEventAccessDays);
  const formattedEventEndDate = format(parsedEventEndDate, 'PPP');
  const formattedEventAppEndDate = format(parsedEventAppEndDate, 'PPP');

  const shouldShowMobileSection = isEventAppEnabled || isCheckInEnabled || isLeadCaptureEnabled;

  const shouldShowWebSection =
    isEventAppEnabled || isWebsiteBuilderEnabled || isRegistrationEnabled || isLiveDisplayEnabled;

  const eventAppAccessExpiryMessage = t`Attendee access expires on ${formattedEventAppEndDate}`;

  const items = [];

  if (shouldShowWebSection) {
    items.push({
      key: 'web-apps',
      label: t`Web Platforms`,
      type: 'group',
      style: { padding: '8px' },
    });
  }

  if (isEventAppEnabled) {
    const link = customDomain ? customDomain.domainName : LINKED_APPLICATIONS_EVENTAPP;
    items.push(
      { type: 'divider' },
      {
        key: 'event-app-web',
        label: (
          <PreviewItem
            title={t`Web-Based Event App`}
            description={eventAppAccessExpiryMessage}
            links={{
              label: `${link}/${eventCode}`,
              url: `https://${link}/${eventCode}`,
            }}
            icon={<LayoutOutlined />}
          />
        ),
      },
    );
  }

  if (isWebsiteBuilderEnabled) {
    const link = customDomain ? `${customDomain.domainName}/website` : LINKED_APPLICATIONS_REGISTER;
    items.push(
      { type: 'divider' },
      {
        key: 'website',
        label: (
          <PreviewItem
            title={t`Registration Website`}
            description={settings?.isPublished ? t`Published` : t`Unpublished`}
            links={{
              label: `${link}/${eventCode}`,
              url: `https://${link}/${eventCode}`,
            }}
            icon={<GlobalOutlined />}
          />
        ),
      },
    );
  }

  if (isRegistrationEnabled) {
    const link = customDomain ? `${customDomain.domainName}/website` : LINKED_APPLICATIONS_REGISTER;
    items.push(
      { type: 'divider' },
      {
        key: 'registration',
        label: (
          <PreviewItem
            title={t`Registration Form`}
            links={{
              label: `${link}/${eventCode}?reg_only=true`,
              url: `https://${link}/${eventCode}?reg_only=true`,
            }}
            icon={<ProfileOutlined />}
          />
        ),
      },
    );
  }

  if (isLiveDisplayEnabled) {
    items.push(
      { type: 'divider' },
      {
        key: 'live-display',
        label: (
          <PreviewItem
            title={t`Live Display`}
            links={{
              label: `${LINKED_APPLICATIONS_LIVEDISPLAY}/${eventCode}`,
              url: `https://${LINKED_APPLICATIONS_LIVEDISPLAY}/${eventCode}`,
            }}
            icon={<FundProjectionScreenOutlined />}
          />
        ),
      },
    );
  }

  if (shouldShowMobileSection) {
    items.push(
      { type: 'divider' },
      {
        key: 'mobile-apps',
        label: t`Mobile Applications`,
        type: 'group',
        style: { padding: '8px' },
      },
    );
  }

  if (isCheckInEnabled) {
    items.push(
      { type: 'divider' },
      {
        key: 'onsite-app',
        label: (
          <PreviewItem
            title={t`BadgeON App`}
            description={t`Organizer access expires on ${formattedEventEndDate}`}
            links={[
              {
                label: t`Download iOS`,
                url: 'https://apps.apple.com/us/app/onsite-by-eventmobi/id1617146323',
                icon: <AppleOutlined />,
              },
              {
                label: t`Download Android`,
                url: 'https://play.google.com/store/apps/details?id=com.eventmobi.onsite',
                icon: <AndroidOutlined />,
              },
            ]}
            imageUri={BadgeONLogo}
            tooltip={t`BadgeON App allows event organizers to check-in attendees during an event`}
            canCopyLink={false}
          />
        ),
      },
    );
  }

  if (isLeadCaptureEnabled) {
    items.push(
      { type: 'divider' },
      {
        key: 'lead-capture-app',
        label: (
          <PreviewItem
            title={t`Lead Capture App`}
            description={t`All user access expires on ${formattedEventEndDate}`}
            links={[
              {
                label: t`Download iOS`,
                url: 'https://apps.apple.com/us/app/lead-capture-by-eventmobi/id6461824837',
                icon: <AppleOutlined />,
              },
              {
                label: t`Download Android`,
                url: 'https://play.google.com/store/apps/details?id=com.eventmobi.leadcapture',
                icon: <AndroidOutlined />,
              },
            ]}
            imageUri={LeadCaptureAppIcon}
            tooltip={t`Take full control of the lead capture process with EventMobi Lead Capture`}
            canCopyLink={false}
          />
        ),
      },
    );
  }

  if (isEventAppEnabled) {
    if (apps?.length > 0) {
      apps.forEach(app =>
        items.push(
          { type: 'divider' },
          {
            key: app.id,
            label: (
              <PreviewItem
                title={app.appNameInStores}
                description={eventAppAccessExpiryMessage}
                links={[
                  app.iosStoreUrl && {
                    label: t`Download iOS`,
                    url: app.iosStoreUrl,
                    icon: <AppleOutlined />,
                  },
                  app.androidStoreUrl && {
                    label: t`Download Android`,
                    url: app.androidStoreUrl,
                    icon: <AndroidOutlined />,
                  },
                ].filter(Boolean)}
                imageUri={app.iconImage?.thumbnailUrl}
                tooltip={t`Mobile version of your Event App`}
                canCopyLink={false}
              />
            ),
          },
        ),
      );
    } else {
      items.push(
        { type: 'divider' },
        {
          key: 'event-app-mobile',
          label: (
            <PreviewItem
              title={t`Mobile Event App`}
              description={eventAppAccessExpiryMessage}
              links={[
                {
                  label: t`Download iOS`,
                  url: 'https://apps.apple.com/us/app/event-app-by-eventmobi/id1276348688',
                  icon: <AppleOutlined />,
                },
                {
                  label: t`Download Android`,
                  url: 'https://play.google.com/store/apps/details?id=com.eventmobi.multieventapp.meaidfa33d52eb4374fdb891ab905fa3a72e7',
                  icon: <AndroidOutlined />,
                },
              ]}
              imageUri={EventAppIcon}
              tooltip={t`Mobile version of your Event App`}
              canCopyLink={false}
            />
          ),
        },
      );
    }
  }

  return { items };
};

const PreviewButton = () => {
  const { items } = useItems();

  return (
    <Dropdown menu={{ items }} placement="bottomLeft">
      <Button icon={<EyeOutlined />}>{t`Preview`}</Button>
    </Dropdown>
  );
};

export default PreviewButton;
